import { useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { TerminalStatus } from '@/types/api';
import type { PosTerminal } from '@/types/api';
import { X, Trash2, Save } from 'lucide-react';
import { useHasPermission } from '@/hooks/usePermission';
import { PERM } from '@/constants/permissions';
import type { TerminalWithHeartbeat } from './types';

interface TerminalSettingsModalProps {
  terminal: TerminalWithHeartbeat;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}

export default function TerminalSettingsModal({ terminal, onClose, onSaved, onDeleted }: TerminalSettingsModalProps) {
  const canDelete = useHasPermission(PERM.TERMINAL_DELETE);

  const [formData, setFormData] = useState({
    name: terminal.name || '',
    status: terminal.status || TerminalStatus.OFFLINE,
    active: terminal.active ?? true,
    ipAddress: terminal.ipAddress || '',
    macAddress: terminal.macAddress || '',
    appVersion: terminal.appVersion || '',
    deviceModel: terminal.deviceModel || '',
    osVersion: terminal.osVersion || '',
  });

  // Snapshot of the form's initial values, to detect unsaved changes on close.
  const initialFormData = useRef(formData);
  const isDirty = JSON.stringify(formData) !== JSON.stringify(initialFormData.current);

  const handleClose = () => {
    if (isDirty && !window.confirm('Discard unsaved changes?')) {
      return;
    }
    onClose();
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isDirty]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState(false);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<PosTerminal>) => apiClient.updateTerminal(terminal.id, data),
    onSuccess: onSaved,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update terminal';
      setErrors({ submit: message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.deleteTerminal(terminal.id),
    onSuccess: onDeleted,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to delete terminal';
      setErrors({ submit: message });
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.ipAddress && !/^(\d{1,3}\.){3}\d{1,3}$/.test(formData.ipAddress)) {
      newErrors.ipAddress = 'Invalid IP address format';
    }

    if (
      formData.macAddress &&
      !/^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/.test(formData.macAddress)
    ) {
      newErrors.macAddress = 'Invalid MAC address format (e.g., AA:BB:CC:DD:EE:FF)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }
    updateMutation.mutate(formData as Partial<PosTerminal>);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleClose}
    >
      <div
        className="card max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Terminal Settings</h2>
            <p className="text-sm text-gray-500 font-mono">{terminal.terminalId}</p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {errors.submit && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="settings-name" className="block text-sm font-medium text-gray-700 mb-1">
              Terminal Name *
            </label>
            <input
              id="settings-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`input w-full ${errors.name ? 'border-red-500' : ''}`}
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-1">Status</span>
              <div className="input w-full bg-gray-50 text-gray-600 flex items-center">
                {terminal.status}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Derived from heartbeats, not editable.
              </p>
            </div>

            <div>
              <label htmlFor="settings-active" className="block text-sm font-medium text-gray-700 mb-1">
                Active
              </label>
              <select
                id="settings-active"
                value={formData.active ? 'true' : 'false'}
                onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })}
                className="input w-full"
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="settings-maintenance"
              type="checkbox"
              checked={formData.status === TerminalStatus.MAINTENANCE}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  status: e.target.checked ? TerminalStatus.MAINTENANCE : TerminalStatus.OFFLINE,
                })
              }
              className="h-4 w-4"
            />
            <label htmlFor="settings-maintenance" className="text-sm font-medium text-gray-700">
              Under maintenance
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="settings-ip" className="block text-sm font-medium text-gray-700 mb-1">
                IP Address
              </label>
              <input
                id="settings-ip"
                type="text"
                value={formData.ipAddress}
                onChange={(e) => setFormData({ ...formData, ipAddress: e.target.value })}
                className={`input w-full font-mono text-sm ${errors.ipAddress ? 'border-red-500' : ''}`}
                placeholder="192.168.1.100"
              />
              {errors.ipAddress && <p className="text-red-500 text-xs mt-1">{errors.ipAddress}</p>}
            </div>

            <div>
              <label htmlFor="settings-mac" className="block text-sm font-medium text-gray-700 mb-1">
                MAC Address
              </label>
              <input
                id="settings-mac"
                type="text"
                value={formData.macAddress}
                onChange={(e) => setFormData({ ...formData, macAddress: e.target.value.toUpperCase() })}
                className={`input w-full font-mono text-sm ${errors.macAddress ? 'border-red-500' : ''}`}
                placeholder="AA:BB:CC:DD:EE:FF"
              />
              {errors.macAddress && <p className="text-red-500 text-xs mt-1">{errors.macAddress}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="settings-version" className="block text-sm font-medium text-gray-700 mb-1">
                Software Version
              </label>
              <input
                id="settings-version"
                type="text"
                value={formData.appVersion}
                onChange={(e) => setFormData({ ...formData, appVersion: e.target.value })}
                className="input w-full"
                placeholder="1.0.0"
              />
            </div>

            <div>
              <label htmlFor="settings-hardware" className="block text-sm font-medium text-gray-700 mb-1">
                Hardware Model
              </label>
              <input
                id="settings-hardware"
                type="text"
                value={formData.deviceModel}
                onChange={(e) => setFormData({ ...formData, deviceModel: e.target.value })}
                className="input w-full"
                placeholder="e.g., iPad Pro 12.9"
              />
            </div>
          </div>

          <div>
            <label htmlFor="settings-os" className="block text-sm font-medium text-gray-700 mb-1">
              OS Version
            </label>
            <input
              id="settings-os"
              type="text"
              value={formData.osVersion}
              onChange={(e) => setFormData({ ...formData, osVersion: e.target.value })}
              className="input w-full"
              placeholder="e.g., iPadOS 17.4"
            />
          </div>

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="btn btn-outline flex-1 h-10">
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1 h-10"
              disabled={updateMutation.isPending}
            >
              <Save className="h-4 w-4 mr-2" />
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>

        {canDelete && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            {!confirmDelete ? (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="btn btn-ghost w-full h-10 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Terminal
              </button>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 space-y-3">
                <p className="text-sm text-red-700">
                  Are you sure you want to delete <strong>{terminal.name}</strong>? This cannot be
                  undone.
                </p>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    className="btn btn-outline flex-1 h-9 text-sm"
                    disabled={deleteMutation.isPending}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteMutation.mutate()}
                    className="btn flex-1 h-9 text-sm bg-red-600 text-white hover:bg-red-700"
                    disabled={deleteMutation.isPending}
                  >
                    {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
